import { useState } from 'react';
import type { FaqContent } from '~/content/types';
import { defaultFaq } from '~/content/shared';
import { Section } from '~/components/layout';
import { Icon } from '~/components/ui';
import { cn } from '~/lib/cn';

/** FAQ: heading left, expandable question list right (Figma 38:6699). */
export function FaqAccordion({
  content = defaultFaq,
  sectionClassName,
  /**
   * Align the two columns to the shared 9-col grid (left cols 1–4, right cols
   * 6–9) instead of the default 50/50 split, so the FAQ lines up with adjacent
   * `col-start-6`/`col-span-4` sections (e.g. the partnerships "promote" block).
   */
  gridAligned = false,
  toggleIcon = 'plus',
  footnote,
}: {
  content?: FaqContent;
  /**
   * Page-level spacing for the section wrapper. Replaces the default padding
   * rather than merging with it: `cn` does not resolve Tailwind conflicts.
   */
  sectionClassName?: string;
  gridAligned?: boolean;
  /** Plus that rotates into a cross (default), or a chevron that flips (payroll Figma). */
  toggleIcon?: 'plus' | 'chevron';
  /** Small print under the question list. */
  footnote?: string;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    // 200px gap to the section above (neighbour contributes ~56px, so pt = 144px)
    // and below (CtaBanner's band starts immediately, so pb = 200px).
    <Section className={sectionClassName ?? '!pt-36 !pb-[200px]'}>
      <div
        className={cn(
          gridAligned ? 'grid grid-cols-9 gap-5 gap-y-10' : 'grid gap-10 lg:grid-cols-2 lg:gap-24',
        )}>
        {/* The heading column is short and the question list is long, so the
            heading sticks while the list scrolls past — the same treatment the
            /help category sidebar gets (`lg:sticky lg:top-24`). `self-start` is
            required: grid items stretch by default, and a stretched item cannot
            stick. `top-24` clears the 64px header with room to spare. */}
        <div
          className={cn(
            'lg:sticky lg:top-24 lg:self-start',
            gridAligned && 'col-span-9 lg:col-span-4',
          )}>
          <h2 className="max-w-md text-h3 font-semibold tracking-[-0.03em] text-balance md:text-h2">
            {content.title}
          </h2>
        </div>
        <div className={cn(gridAligned && 'col-span-9 lg:col-span-4 lg:col-start-6')}>
          <ul>
            {content.items.map((item, i) => {
              const open = openIndex === i;
              return (
                <li
                  key={item.question}
                  className={cn(
                    i > 0 && 'border-t border-black-6',
                    item.mobileOnly && 'md:hidden',
                  )}>
                  {/* Each question is an H3 under the section's H2, which is both the
                    ARIA accordion pattern and what the SEO briefs specify for the
                    FAQ outline. The heading wraps the button rather than sitting
                    inside it: `button` takes phrasing content only, so an `h3`
                    nested in one is invalid and gets reparented by the parser. */}
                  <h3>
                    <button
                      type="button"
                      onClick={() => setOpenIndex(open ? null : i)}
                      aria-expanded={open}
                      className="flex w-full items-center justify-between gap-6 py-6 text-start">
                      <span className="text-h5 leading-snug font-semibold">{item.question}</span>
                      {toggleIcon === 'chevron' ? (
                        <Icon
                          name="chevron-down"
                          className={cn(
                            'size-6 shrink-0 transition-transform',
                            open && 'rotate-180',
                          )}
                        />
                      ) : (
                        <svg
                          viewBox="0 0 24 24"
                          className={cn(
                            'size-6 shrink-0 text-grey-700 transition-transform',
                            open && 'rotate-45',
                          )}
                          stroke="currentColor"
                          strokeWidth="1.6"
                          fill="none">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                      )}
                    </button>
                  </h3>
                  {open && <p className="pb-6 text-body text-grey-900">{item.answer}</p>}
                </li>
              );
            })}
          </ul>
          {footnote && <p className="mt-14 text-body-m text-grey-900 md:mt-20">{footnote}</p>}
        </div>
      </div>
    </Section>
  );
}
